import { useState } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export function VoteButtons({ targetType, targetId, initialScore = 0, initialMyVote = 0, layout = "vertical", size = "sm", onChange }) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [score, setScore] = useState(initialScore);
    const [myVote, setMyVote] = useState(initialMyVote);
    const [busy, setBusy] = useState(false);

    async function cast(v) {
        if (!user) {
            toast.error("Sign in to vote");
            navigate("/login");
            return;
        }
        if (busy) return;
        setBusy(true);
        const next = myVote === v ? 0 : v; // clicking same arrow clears
        try {
            const { data } = await api.post("/social/votes", { target_type: targetType, target_id: targetId, value: next });
            setScore(data.score);
            setMyVote(data.my_vote ?? next);
            onChange?.(data.score, data.my_vote);
        } catch (e) {
            toast.error("Vote failed");
        } finally {
            setBusy(false);
        }
    }

    const dim = size === "lg" ? "w-5 h-5" : "w-4 h-4";
    const txt = size === "lg" ? "text-sm" : "text-xs";
    const wrap = layout === "vertical" ? "flex-col" : "flex-row";

    return (
        <div className={`inline-flex ${wrap} items-center gap-1 select-none`} data-testid={`vote-${targetType}-${targetId}`}>
            <button
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); cast(1); }}
                className={`p-1 rounded transition ${myVote === 1 ? "text-[var(--ok-accent)]" : "opacity-50 hover:opacity-100"}`}
                aria-label="Upvote"
                data-testid={`upvote-${targetId}`}
            >
                <ChevronUp className={dim} />
            </button>
            <span className={`font-mono ${txt} tabular-nums ${myVote === 1 ? "text-[var(--ok-accent)]" : myVote === -1 ? "text-red-400" : "opacity-70"}`}>{score}</span>
            <button
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); cast(-1); }}
                className={`p-1 rounded transition ${myVote === -1 ? "text-red-400" : "opacity-50 hover:opacity-100"}`}
                aria-label="Downvote"
                data-testid={`downvote-${targetId}`}
            >
                <ChevronDown className={dim} />
            </button>
        </div>
    );
}
